import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Modal, TextArea, Form } from 'semantic-ui-react'
import { ArrowCounterClockwise } from 'phosphor-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import {
    buildSectionQuestionEditChanges,
    findAuditSectionById,
} from './customer-claim-inspection-utils'

export const parseSectionStatusResponse = response => {
    if (Array.isArray(response)) {
        return response
    }
    const candidates = [
        response?.Data,
        response?.data,
        response?.Items,
        response?.items,
        response?.Result,
        response?.result,
    ]
    const match = candidates.find(Array.isArray)
    return match || []
}

const parseWorkflowFlag = value => {
    if (value === true || value === 1 || value === '1') return true
    if (value === false || value === 0 || value === '0') return false
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        if (normalized === 'true' || normalized === 'yes') return true
        if (normalized === 'false' || normalized === 'no') return false
    }
    return !!value
}

const parseSuperAdminFlag = (...values) =>
    values.some(value => parseWorkflowFlag(value))

export const resolveWorkflowUserContext = user => {
    const companies = Array.isArray(user?.Companies) ? user.Companies : []
    const company =
        companies.find(
            entry =>
                Number(entry?.CompanyId) === Number(user?.CompanyId) &&
                user?.CompanyId != null,
        ) ||
        companies[0] ||
        {}

    const roleNames = [
        user?.Role,
        user?.role,
        company?.Role,
        company?.role,
    ]
        .map(value => String(value || '').toLowerCase())
        .filter(Boolean)

    const roleIds = [
        user?.RoleId,
        user?.RoleID,
        company?.RoleId,
        company?.RoleID,
        ...companies.map(entry => entry?.RoleId ?? entry?.RoleID),
    ]
        .map(value => Number(value) || 0)
        .filter(Boolean)

    return {
        isSuperAdmin:
            parseSuperAdminFlag(
                user?.IsSuperAdmin,
                user?.isSuperAdmin,
                user?.IsSystemAdmin,
                company?.IsSuperAdmin,
                company?.isSuperAdmin,
                user?.Capabilities?.IsSuperAdmin,
                user?.Capabilities?.isSuperAdmin,
                ...companies.map(entry => entry?.IsSuperAdmin ?? entry?.isSuperAdmin),
            ) ||
            roleIds.some(id => id === 100) ||
            roleNames.some(
                name =>
                    name.includes('super admin') || name.includes('superadmin'),
            ),
        userId:
            user?.UserId ??
            user?.unique_id ??
            user?.Id ??
            user?.SystemUserId ??
            company?.UserId ??
            null,
        userRoleId:
            user?.RoleId ?? user?.RoleID ?? company?.RoleId ?? company?.RoleID ?? null,
    }
}

export const normalizeSectionStatusEntry = entry => {
    if (!entry || typeof entry !== 'object') return null
    return {
        sectionId: entry.SectionId ?? entry.sectionId ?? '',
        phaseOrder: Number(entry.PhaseOrder ?? entry.phaseOrder ?? 0),
        completeLabel: String(
            entry.CompleteLabel ?? entry.completeLabel ?? 'Complete',
        ).trim(),
        isCompleted: parseWorkflowFlag(
            entry.IsCompleted ?? entry.isCompleted,
        ),
        completedByName: String(
            entry.CompletedByName ?? entry.completedByName ?? '',
        ).trim(),
        completedOn: entry.CompletedOn ?? entry.completedOn ?? null,
        isCurrentPhase: parseWorkflowFlag(
            entry.IsCurrentPhase ?? entry.isCurrentPhase,
        ),
        canCurrentUserComplete: parseWorkflowFlag(
            entry.CanCurrentUserComplete ?? entry.canCurrentUserComplete,
        ),
        canCurrentUserEdit: parseWorkflowFlag(
            entry.CanCurrentUserEdit ?? entry.canCurrentUserEdit,
        ),
        assignedRoleId: Number(
            entry.AssignedRoleId ?? entry.assignedRoleId ?? 0,
        ) || null,
        assignedUserId: Number(
            entry.AssignedUserId ?? entry.assignedUserId ?? 0,
        ) || null,
    }
}

export const resolveSectionWorkflowStatus = (
    statuses,
    sectionId,
    phaseOrder = null,
) => {
    const normalized = (statuses || [])
        .map(normalizeSectionStatusEntry)
        .filter(Boolean)

    if (sectionId != null && String(sectionId).trim() !== '') {
        const sectionKey = String(sectionId).trim()
        const byId = normalized.find(
            item => String(item.sectionId).trim() === sectionKey,
        )
        if (byId) return byId
    }

    if (phaseOrder != null) {
        return normalized.find(item => item.phaseOrder === phaseOrder) || null
    }

    return null
}

const phaseHasLiveSection = (liveSectionIdsByPhase, phaseOrder) => {
    if (
        !Object.prototype.hasOwnProperty.call(liveSectionIdsByPhase, phaseOrder)
    ) {
        return true
    }
    return Boolean(liveSectionIdsByPhase[phaseOrder])
}

// Shared Customer Claim setup always includes Quality Review. Non RMA audits
// can omit that section, so a missing live id must drop the phase. The next
// remaining incomplete phase becomes current once every earlier live phase
// is already complete.
const alignCurrentPhaseWithLiveSections = statuses => {
    const ordered = [...statuses].sort((a, b) => a.phaseOrder - b.phaseOrder)
    const firstIncompleteIndex = ordered.findIndex(item => !item.isCompleted)
    return ordered.map((item, index) => {
        if (item.isCompleted || firstIncompleteIndex < 0) {
            return { ...item, isCurrentPhase: false }
        }
        return { ...item, isCurrentPhase: index === firstIncompleteIndex }
    })
}

export const overlayWorkflowStatusesOntoLiveSections = (
    statuses,
    liveSectionIdsByPhase = {},
) => {
    const remaining = (statuses || [])
        .map(entry => {
            const normalized = normalizeSectionStatusEntry(entry)
            if (!normalized) return null
            if (!phaseHasLiveSection(liveSectionIdsByPhase, normalized.phaseOrder)) {
                return null
            }
            const liveId = liveSectionIdsByPhase[normalized.phaseOrder]
            if (liveId) {
                return { ...normalized, sectionId: String(liveId) }
            }
            return normalized
        })
        .filter(Boolean)
    return alignCurrentPhaseWithLiveSections(remaining)
}

export const canUserCompleteSection = status => {
    if (!status?.isCurrentPhase) return false
    return !!status.canCurrentUserComplete
}

export const canUserEditCompletedSection = status => {
    if (!status?.isCompleted) return false
    return !!status.canCurrentUserEdit
}

/** Uncomplete uses the same SectionStatus flag as post-complete edit. */
export const canUserUncompleteSection = status =>
    canUserEditCompletedSection(status)

export const buildSectionStatusMap = statuses => {
    const map = new Map();
    (statuses || []).forEach(raw => {
        const entry = normalizeSectionStatusEntry(raw)
        if (!entry?.sectionId) return
        map.set(String(entry.sectionId), entry)
    })
    return map
}

export const getSectionWorkflowBadge = status => {
    if (!status) return null
    if (status.isCompleted) {
        return { label: 'COMPLETED', variant: 'done' }
    }
    if (status.isCurrentPhase) {
        return { label: 'CURRENT PHASE', variant: 'current' }
    }
    return { label: 'LOCKED', variant: 'locked' }
}

export const isSectionWorkflowLocked = status => {
    if (!status) return false
    if (status.isCompleted) return true
    return !status.isCurrentPhase
}

/** True when section fields should be read-only (locked phase or no complete/edit permission). */
export const isSectionWorkflowFieldsDisabled = status => {
    if (!status) return false
    if (status.isCompleted) {
        return !canUserEditCompletedSection(status)
    }
    if (!status.isCurrentPhase) return true
    return !canUserCompleteSection(status)
}

export const getWaitingOnLabel = (status, allStatuses) => {
    if (!status?.phaseOrder || status.phaseOrder <= 1) {
        return 'previous phase'
    }
    const previous = (allStatuses || [])
        .filter(item => item && item.phaseOrder < status.phaseOrder)
        .sort((a, b) => b.phaseOrder - a.phaseOrder)[0]
    return previous?.completeLabel || 'previous phase'
}

export const reportCompletedSectionEdits = async ({
    auditId,
    templateUniqueId,
    beforeAudit,
    afterAudit,
    workflowStatuses = [],
    reportEdit,
}) => {
    if (
        !auditId ||
        !templateUniqueId ||
        !beforeAudit ||
        !afterAudit ||
        typeof reportEdit !== 'function'
    ) {
        return []
    }

    const payloads = (workflowStatuses || [])
        .map(normalizeSectionStatusEntry)
        .filter(Boolean)
        .filter(status => status.isCompleted && status.canCurrentUserEdit)
        .map(status => {
            const beforeSection = findAuditSectionById(
                beforeAudit,
                status.sectionId,
            )
            const afterSection = findAuditSectionById(afterAudit, status.sectionId)
            if (!beforeSection || !afterSection) {
                return null
            }

            const changes = buildSectionQuestionEditChanges(
                beforeSection,
                afterSection,
            )
            if (!changes.length) {
                return null
            }

            return {
                AuditId: Number(auditId),
                TemplateUniqueId: Number(templateUniqueId),
                SectionId: String(status.sectionId),
                Changes: changes,
            }
        })
        .filter(Boolean)

    return Promise.all(payloads.map(payload => reportEdit(payload)))
}

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 0.3rem 0.65rem;
    border-radius: 999px;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
    background: ${({ $variant }) => {
        if ($variant === 'done') return '#10B981'
        if ($variant === 'current') return '#ffffff'
        return 'rgba(255, 255, 255, 0.2)'
    }};
    color: ${({ $variant }) => {
        if ($variant === 'current') return '#1E40AF'
        return '#ffffff'
    }};
    border: 1px solid
        ${({ $variant }) => {
            if ($variant === 'current') return '#ffffff'
            if ($variant === 'done') return 'rgba(255, 255, 255, 0.35)'
            return 'rgba(255, 255, 255, 0.45)'
        }};
`

const SectionWorkflowFooter = styled.div`
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
`

const CompletedInfo = styled.span`
    color: #10b981;
    font-weight: 600;
    font-size: 0.8rem;
`

const WaitingText = styled.span`
    color: #64748b;
    font-size: 0.8rem;
    font-style: italic;
`

const FooterHint = styled.span`
    color: #64748b;
    font-size: 0.8rem;
`

const FooterActions = styled.div`
    margin-left: auto;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`

const CompleteButton = styled(AppPrimaryButton)`
    &&& {
        background: #10b981 !important;
        border-color: #10b981 !important;
    }

    &&&:hover:not(:disabled) {
        background: #059669 !important;
        border-color: #059669 !important;
    }
`

const UncompleteButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid #dc2626;
    border-radius: 6px;
    background: #fff;
    color: #dc2626;
    font-size: 0.85rem;
    font-weight: 500;
    line-height: 1.2;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: none;

    &:hover:not(:disabled) {
        background: #fef2f2;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`

const ClaimBanner = styled.div`
    background: linear-gradient(90deg, #eef2ff, #fff);
    border: 1px solid #4f46e5;
    border-left: 4px solid #4f46e5;
    border-radius: 6px;
    padding: 0.85rem 1rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
`

const ClaimNumberChip = styled.div`
    font-family: 'Consolas', 'JetBrains Mono', monospace;
    background: #4f46e5;
    color: #fff;
    padding: 0.35rem 0.85rem;
    border-radius: 4px;
    font-size: 1rem;
    font-weight: 700;
`

const ClaimBannerLabel = styled.div`
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`

const ClaimBannerName = styled.div`
    font-weight: 600;
    font-size: 0.95rem;
    color: #0f172a;
`

const LockedSectionBody = styled.div`
    pointer-events: ${({ $locked }) => ($locked ? 'none' : 'auto')};

    /* Locked phases stay non-editable, but uploaded images can still be opened. */
    ${({ $locked }) =>
        $locked
            ? `
        .claim-media-preview .flex-wrap.flex-row,
        .claim-media-preview .preview-media,
        .claim-media-preview .media-item,
        .horizontal-media-view-btn {
            pointer-events: auto;
            cursor: pointer;
        }

        .claim-media-preview .rotate-icon {
            pointer-events: auto;
            cursor: pointer;
        }

        .claim-media-preview .delete-icon {
            display: none !important;
            pointer-events: none !important;
        }
    `
            : ''}
`

const formatCompletedOn = value => {
    if (!value) return ''
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) {
        return String(value)
    }
    return date.toLocaleString()
}

export const ClaimSectionStatusBadge = ({ status }) => {
    const badge = getSectionWorkflowBadge(status)
    if (!badge) return null
    return (
        <StatusBadge $variant={badge.variant}>{badge.label}</StatusBadge>
    )
}

export const ClaimSectionWorkflowBody = ({ status, children }) => {
    const locked = isSectionWorkflowFieldsDisabled(status)
    return <LockedSectionBody $locked={locked}>{children}</LockedSectionBody>
}

export const ClaimNumberBanner = ({ claimNumber, customerName }) => {
    if (!claimNumber) return null
    return (
        <ClaimBanner>
            <ClaimNumberChip>{claimNumber}</ClaimNumberChip>
            <div>
                <ClaimBannerLabel>Customer</ClaimBannerLabel>
                <ClaimBannerName>{customerName || '—'}</ClaimBannerName>
            </div>
        </ClaimBanner>
    )
}

export const ClaimSectionWorkflowFooter = ({
    status,
    allStatuses = [],
    onComplete,
    onUncomplete,
    completing = false,
}) => {
    if (!status) return null

    const userCanComplete = canUserCompleteSection(status)

    if (status.isCompleted) {
        const completedAt = formatCompletedOn(status.completedOn)
        const completedBy = status.completedByName || '—'
        const userCanUncomplete = canUserUncompleteSection(status)
        return (
            <SectionWorkflowFooter>
                <CompletedInfo>
                    ✓ Completed by {completedBy}
                    {completedAt ? ` · ${completedAt}` : ''}
                </CompletedInfo>
                {userCanUncomplete && onUncomplete ? (
                    <FooterActions>
                        <UncompleteButton
                            type="button"
                            disabled={completing}
                            onClick={() => onUncomplete(status)}
                        >
                            <ArrowCounterClockwise size={18} weight="bold" />
                            Uncomplete
                        </UncompleteButton>
                    </FooterActions>
                ) : null}
            </SectionWorkflowFooter>
        )
    }

    if (status.isCurrentPhase && onComplete) {
        return (
            <SectionWorkflowFooter>
                <FooterHint>
                    {userCanComplete
                        ? 'Open for you — fill the fields above, then click Complete.'
                        : 'This phase is active — only the assigned role can complete it.'}
                </FooterHint>
                <FooterActions>
                    <CompleteButton
                        type="button"
                        disabled={completing || !userCanComplete}
                        onClick={() => onComplete(status)}
                    >
                        {status.completeLabel || 'Complete'} ✓
                    </CompleteButton>
                </FooterActions>
            </SectionWorkflowFooter>
        )
    }

    const waitingOn = getWaitingOnLabel(status, allStatuses)
    return (
        <SectionWorkflowFooter>
            <WaitingText>
                Waiting on <strong>{waitingOn}</strong> to complete
            </WaitingText>
        </SectionWorkflowFooter>
    )
}

const CompleteNotesField = styled.div`
    width: 100%;
    margin-top: 0.75rem;

    .ui.form {
        width: 100%;
    }

    .field {
        width: 100%;
        margin: 0;
    }

    textarea {
        width: 100% !important;
        min-height: 5.5rem;
        box-sizing: border-box;
        display: block;
        padding: 0.75rem 0.85rem;
        background: #fff;
        border: 1px solid rgba(34, 36, 38, 0.15);
        border-radius: 0.28571429rem;
        color: rgba(0, 0, 0, 0.87);
        font-size: 0.95rem;
        line-height: 1.4;
        resize: vertical;

        &::placeholder {
            color: rgba(0, 0, 0, 0.4);
        }

        &:focus {
            border-color: #85b7d9;
            outline: none;
            box-shadow: 0 0 0 1px #85b7d9 inset;
        }
    }
`

export const ClaimSectionCompleteModal = ({
    open,
    title,
    body,
    notes,
    onNotesChange,
    onCancel,
    onConfirm,
    confirming = false,
    confirmLabel = 'Yes, complete',
}) => (
    <Modal open={open} size="small" onClose={onCancel}>
        <Modal.Header>{title}</Modal.Header>
        <Modal.Content>
            {body ? <p style={{ color: '#334155' }}>{body}</p> : null}
            <CompleteNotesField>
                <Form>
                    <Form.Field>
                        <label htmlFor="claim-section-complete-notes">
                            Notes (optional)
                        </label>
                        <TextArea
                            id="claim-section-complete-notes"
                            placeholder="e.g. Claim verified with customer over phone"
                            value={notes}
                            onChange={(_, data) => onNotesChange(data.value)}
                            rows={3}
                        />
                    </Form.Field>
                </Form>
            </CompleteNotesField>
        </Modal.Content>
        <Modal.Actions>
            <AppSecondaryButton type="button" onClick={onCancel}>
                Cancel
            </AppSecondaryButton>
            <CompleteButton type="button" disabled={confirming} onClick={onConfirm}>
                {confirmLabel}
            </CompleteButton>
        </Modal.Actions>
    </Modal>
)

export const useClaimSectionWorkflowModal = ({
    onConfirmComplete,
    onConfirmUncomplete,
}) => {
    const [modalState, setModalState] = useState(null)
    const [notes, setNotes] = useState('')
    const [confirming, setConfirming] = useState(false)

    const openComplete = status => {
        setNotes('')
        setModalState({
            mode: 'complete',
            status,
            title: `Complete: ${status.completeLabel}?`,
            body: 'The next team will be notified by email once you confirm. This action is reversible — Super Admin can roll it back if needed.',
            confirmLabel: 'Yes, complete',
        })
    }

    const openUncomplete = status => {
        setNotes('')
        setModalState({
            mode: 'uncomplete',
            status,
            title: `Uncomplete: ${status.completeLabel}?`,
            body: 'This will reset the phase and notify all stakeholders. Cannot uncomplete if a later phase already has completions.',
            confirmLabel: 'Yes, uncomplete',
        })
    }

    const closeModal = () => {
        if (confirming) return
        setModalState(null)
        setNotes('')
    }

    const handleConfirm = async () => {
        if (!modalState?.status) return
        setConfirming(true)
        try {
            if (modalState.mode === 'uncomplete') {
                await onConfirmUncomplete?.(modalState.status)
            } else {
                await onConfirmComplete?.(modalState.status, notes)
            }
            setModalState(null)
            setNotes('')
        } finally {
            setConfirming(false)
        }
    }

    const modal = (
        <ClaimSectionCompleteModal
            open={Boolean(modalState)}
            title={modalState?.title || ''}
            body={modalState?.body || ''}
            notes={notes}
            onNotesChange={setNotes}
            onCancel={closeModal}
            onConfirm={handleConfirm}
            confirming={confirming}
            confirmLabel={modalState?.confirmLabel || 'Yes, complete'}
        />
    )

    return useMemo(
        () => ({
            modal,
            openComplete,
            openUncomplete,
            confirming,
        }),
        [modal, confirming],
    )
}
